import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { Call as CallIcon, CallEnd as CallEndIcon } from '@mui/icons-material'
import { useLessonCall } from '@/webrtc/callContext'

/** Короткий сигнал входящего звонка (как звук входа в урок в useLessonRealtime). */
function playRingtone(): (() => void) | undefined {
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return undefined

  const ctx = new AC()
  let stopped = false

  const beep = () => {
    if (stopped) return
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(660, ctx.currentTime)
    gain.gain.setValueAtTime(0.08, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.5)
  }

  beep()
  const timer = window.setInterval(beep, 2500)
  // Звонить бесконечно нельзя: диалог может висеть до конца урока, если
  // ученик отошёл от компьютера.
  const stopTimer = window.setTimeout(() => {
    stopped = true
    window.clearInterval(timer)
  }, 30_000)

  return () => {
    stopped = true
    window.clearInterval(timer)
    window.clearTimeout(stopTimer)
    void ctx.close().catch(() => undefined)
  }
}

/**
 * Входящий звонок. Кнопка «Принять» обязательна: Safari и мобильные браузеры
 * отдают микрофон и звук только после жеста пользователя, автоматически
 * подключиться к звонку нельзя.
 */
export function IncomingCallDialog() {
  const { t } = useTranslation()
  const call = useLessonCall()
  const stopRingtoneRef = useRef<(() => void) | undefined>(undefined)

  const isOpen = call.status === 'incoming'

  useEffect(() => {
    if (!isOpen) return

    stopRingtoneRef.current = playRingtone()
    return () => {
      stopRingtoneRef.current?.()
      stopRingtoneRef.current = undefined
    }
  }, [isOpen])

  const isVideo = call.session?.type === 'video'

  return (
    <Dialog open={isOpen} onClose={call.declineCall} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isVideo ? t('lessonCall.incoming.titleVideo') : t('lessonCall.incoming.titleAudio')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {t('lessonCall.incoming.description')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<CallEndIcon />} onClick={call.declineCall} color="inherit">
          {t('lessonCall.incoming.decline')}
        </Button>
        <Button
          startIcon={<CallIcon />}
          variant="contained"
          onClick={() => void call.acceptCall()}
        >
          {t('lessonCall.incoming.accept')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
